import { useRef, useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import apiClient from '../../utils/axiosPetition';
import { alertaCargandoProceso, alertaGeneral } from '../../utils/alertas';
import { useDataUser } from '../../context/dataUserContext';
import InputFile from '../../utils/InputFile';
import SignatureCanvas from 'react-signature-canvas';

const CONSENTIMIENTO_TITULO = 'SERVICIOS DE REDPLIKA';
const CONSENTIMIENTO_SUBTITULO = 'RED COLOMBIANA DE PRESTACIÓN Y CUIDADOS EN SALUD S.A.S.';
const EMPRESA_CONSENTIMIENTO = 'Redplika';

const ConsentimientoInformado = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { appointmentId: paramAppointmentId } = useParams();
  const sigCanvasRef = useRef(null);
  const [typeUserSignature, setTypeUserSignature] = useState("paciente");
  const [appointmentInfo, setAppointmentInfo] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const appointmentId = paramAppointmentId || location.state?.appointmentId || null;
  const [dataRepresentante, setDataRepresentante] = useState({
    fullName: "",
    idNumber: "",
    relationship: "",
  });
  const { role, clinicId, setIsAuth } = useDataUser();
  
  // Manejar caso cuando no hay usuario autenticado (ruta pública)
  const isAuthenticated = role !== null && role !== undefined;

  // Prevenir scroll en móviles durante la firma
  useEffect(() => {
    const preventScroll = (e) => {
      // Prevenir scroll en eventos táctiles dentro del área de firma
      if (e.target && e.target.closest('[data-signature-canvas]')) {
        e.preventDefault();
      }
    };

    // Agregar event listeners para prevenir scroll
    document.addEventListener('touchmove', preventScroll, { passive: false });
    document.addEventListener('touchstart', preventScroll, { passive: false });

    return () => {
      document.removeEventListener('touchmove', preventScroll);
      document.removeEventListener('touchstart', preventScroll);
    };
  }, []);

  // useEffect para obtener información de la cita (solo si está autenticado)
  useEffect(() => {
    if (!appointmentId || !isAuthenticated) {
      return;
    }
    apiClient.get(`${process.env.REACT_APP_BACKEND_URL}/api/clinic/appointment-info/${appointmentId}`)
      .then(res => {
        setAppointmentInfo(res.data);
      }).catch(err => {
        alertaGeneral({
          titulo: 'Error',
          messageHtml: 'Ocurrió un error al obtener la información de la cita.',
          isError: true
        });
      })
  }, [appointmentId, isAuthenticated]);

  // Mostrar mensaje de error si no hay appointmentId en lugar de redirigir
  if (!appointmentId) {
    return (
      <div className="w-[90vw] md:w-[80vw] mx-auto border border-[#6e787aff] rounded-md flex flex-col items-center justify-center max-h-fit overflow-auto bg-gray-100 p-8 my-4 gap-4">
        <div className="text-center">
          <h2 className="text-2xl text-red-600 mb-4">Error</h2>
          <p className="text-gray-700 mb-4">No se encontró el ID de la cita en la URL.</p>
          <p className="text-sm text-gray-500 mb-6">
            Asegúrate de acceder desde el enlace correcto o contacta al administrador.
          </p>
          <button
            onClick={() => navigate(isAuthenticated ? `/${role}/teleconsultas` : '/')}
            className="px-4 py-2 bg-[#1b7ea6ff] text-white rounded-md hover:bg-[#205087ff] transition-colors"
          >
            {isAuthenticated ? 'Volver a Teleconsultas' : 'Ir al Inicio'}
          </button>
        </div>
      </div>
    );
  }

  const onHandleChangeDataRepresentante = (e) => {
    const { name, value } = e.target;
    setDataRepresentante((prevData) => ({
      ...prevData,
      [name]: value
    }));
  }

  const changeUserSignature = (e) => {
    const { value } = e.target;
    setTypeUserSignature(value);
    setDataRepresentante({
      fullName: "",
      idNumber: "",
      relationship: "",
    });
  }

  const clearSignature = () => {
    if (sigCanvasRef.current) {
      sigCanvasRef.current.clear();
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadedFiles((prevFiles) => [...prevFiles, file]);
    }
  };

  const handleFileRemove = (fileIndex) => {
    setUploadedFiles((prevFiles) => prevFiles.filter((_, index) => index !== fileIndex));
  };

  const handleAccept = async () => {
    // Verificar si el canvas está vacío
    if (sigCanvasRef.current.isEmpty()) {
      alertaGeneral({
        titulo: 'Advertencia',
        messageHtml: 'Por favor, firme para continuar.',
        isWarning: true
      });
      return;
    }

    if (typeUserSignature === "representante" && (!dataRepresentante.fullName || !dataRepresentante.idNumber || !dataRepresentante.relationship)) {
      alertaGeneral({
        titulo: 'Advertencia',
        messageHtml: 'Por favor, complete todos los campos del representante legal.',
        isWarning: true
      });
      return;
    }

    alertaCargandoProceso({
      titulo: "Enviando consentimiento...",
      messageHtml: "Por favor, espere.",
      funcionAsync: async () => {
        try {
          // Crear un FormData
          const formData = new FormData();

          // Agregar los datos del paciente
          const patientInfo = {
            patientId: appointmentInfo.patientId,
            firstName: appointmentInfo.firstName,
            secondName: appointmentInfo.secondName,
            firstLastName: appointmentInfo.firstLastName,
            secondLastName: appointmentInfo.secondLastName,
            dob: appointmentInfo.dob,
            birthCountry: appointmentInfo.birthCountry,
            residenceCountry: appointmentInfo.residenceCountry,
            department: appointmentInfo.department,
            city: appointmentInfo.city,
            street: appointmentInfo.street,
            exteriorNumber: appointmentInfo.exteriorNumber,
            interiorNumber: appointmentInfo.interiorNumber,
            neighborhood: appointmentInfo.neighborhood,
            gender: appointmentInfo.gender,
            cellNumber: appointmentInfo.cellNumber,
            email: appointmentInfo.email,
            idType: appointmentInfo.idType,
            documentNumber: appointmentInfo.documentNumber,
            hasMedicalInsurance: appointmentInfo.hasMedicalInsurance,
            insuranceName: appointmentInfo.insuranceName,
            insuranceNumber: appointmentInfo.insuranceNumber,
            hasCarer: appointmentInfo.hasCarer,
            carerFirstName: appointmentInfo.carerFirstName,
            carerSecondName: appointmentInfo.carerSecondName,
            carerFirstLastName: appointmentInfo.carerFirstLastName,
            carerSecondLastName: appointmentInfo.carerSecondLastName,
            carerRelationship: appointmentInfo.carerRelationship,
            carerIdType: appointmentInfo.carerIdType,
            carerIdNumber: appointmentInfo.carerIdNumber,
          };

          // Obtener clinicId de múltiples fuentes posibles
          const clinicIdFromAppointment = appointmentInfo?.clinicId || 
                                        appointmentInfo?.doctor?.clinicId || 
                                        appointmentInfo?.clinic?._id ||
                                        appointmentInfo?.clinic?.id;
          
          // Intentar obtener clinicId del localStorage como fallback
          const clinicIdFromStorage = localStorage.getItem('clinicId');
          
          const clinicIdToUse = clinicIdFromAppointment || 
                               clinicIdFromStorage || 
                               clinicId;
          // Validar que clinicId esté presente
          if (!clinicIdToUse) {
            // Intentar obtener clinicId del token JWT como último recurso
            try {
              const token = localStorage.getItem('token');
              if (token) {
                const decoded = JSON.parse(atob(token.split('.')[1]));
                const clinicIdFromToken = decoded.clinicId;
                
                
                if (clinicIdFromToken) {
                  // Usar clinicId del token
                  formData.append('clinicId', clinicIdFromToken);
                } else {
                  throw new Error('No clinicId en token');
                }
              } else {
                throw new Error('No hay token');
              }
            } catch (error) {
              
              alertaGeneral({
                titulo: 'Error de configuración',
                messageHtml: 'No se pudo obtener el ID de la clínica. Por favor, contacta al administrador.',
                isError: true
              });
              return;
            }
          } else {
            // Usar clinicId obtenido de las fuentes anteriores
            formData.append('clinicId', clinicIdToUse);
          }

          // Agregar la información al FormData
          formData.append('appointmentId', appointmentId);
          formData.append('patientInfo', JSON.stringify(patientInfo));
          formData.append('doctorId', appointmentInfo.doctorId);

          // Agregar la firma como string base64
          const signatureDataUrl = sigCanvasRef.current.toDataURL('image/png');
          formData.append('signature', signatureDataUrl);
          formData.append('userSigning', typeUserSignature);
          formData.append('dataRepresentante', dataRepresentante ? JSON.stringify(dataRepresentante) : null);

          // Agregar los archivos cargados
          uploadedFiles.forEach((file, index) => {
            formData.append(`files[${index}]`, file);
          });

          // Enviar la petición al backend con timeout extendido para archivos grandes
          const response = await apiClient.post(
            `${process.env.REACT_APP_BACKEND_URL}/api/pdf/consentimientoInformado`, 
            formData,
            {
              timeout: 120000, // 2 minutos de timeout
              // No establecer Content-Type manualmente - axios lo establecerá automáticamente con el boundary correcto
            }
          );

          // Procesar la respuesta como antes
          if (response.status === 200) {
            const { contentType, buffer } = response.data;

            // Decodificar el Base64 a un Uint8Array
            const fileBuffer = Uint8Array.from(atob(buffer), (c) => c.charCodeAt(0));

            // Convertir el Uint8Array en un Blob
            const blob = new Blob([fileBuffer], { type: contentType });

            // Crear una URL de descarga para el Blob
            const url = window.URL.createObjectURL(blob);

            // // Crear un enlace temporal para iniciar la descarga
            // const link = document.createElement('a');
            // link.href = url;
            // link.setAttribute('download', `consentimiento-${appointmentId}.pdf`);
            // document.body.appendChild(link);
            // link.click();
            // link.remove(); // Eliminar el enlace después de hacer clic
            
            
            
            // Verificar que el token existe antes de redirigir
            const token = localStorage.getItem('token');
            
            
            
            
            if (!token) {
              
              alertaGeneral({
                titulo: 'Sesión Expirada',
                messageHtml: 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.',
                isError: true
              }).then(() => {
                navigate('/login');
              });
              return;
            }
            
            // Si el usuario está autenticado pero el contexto no lo refleja, actualizar el contexto
            if (token && !isAuthenticated) {
              
              setIsAuth(true);
            }
            
            alertaGeneral({
              titulo: '¡Consentimiento registrado!',
              messageHtml: 'El consentimiento fue firmado correctamente. Podrá ingresar a la videoconsulta desde Teleconsultas, 10 minutos antes de la hora agendada.',
            });
            
            setTimeout(() => {
              if (token) {
                setIsAuth(true);
              }
              navigate(`/${role || 'patient'}/teleconsultas`, { replace: true });
            }, 1500);

          } else {
            
            alertaGeneral({
              titulo: 'Error',
              messageHtml: 'La respuesta del servidor no contiene un archivo PDF válido.',
              isError: true
            });
          }
        } catch (error) {
          
          
          // Mensaje de error más específico según el tipo de error
          let errorMessage = 'Hubo un problema al enviar el consentimiento.';
          
          if (error.code === 'NETWORK_ERROR' || error.message === 'Network Error') {
            errorMessage = 'Error de conexión con el servidor. Por favor, verifica tu conexión a internet e intenta nuevamente.';
          } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
            errorMessage = 'La petición tardó demasiado tiempo. Por favor, intenta nuevamente. Si el problema persiste, contacta al administrador.';
          } else if (error.response?.status === 502) {
            errorMessage = 'El servidor no está respondiendo correctamente. Por favor, intenta nuevamente en unos momentos.';
          } else if (error.response?.status === 400) {
            errorMessage = error.response?.data?.error || 'Los datos enviados no son válidos. Por favor, verifica la información e intenta nuevamente.';
          } else if (error.response?.data?.message) {
            errorMessage = error.response.data.message;
          }
          
          alertaGeneral({
            titulo: 'Error',
            messageHtml: errorMessage,
            isError: true
          });

        }
      }
    })
  };

  const handleDecline = () => {
    alertaGeneral({
      titulo: 'Consentimiento Rechazado',
      messageHtml: 'Recuerde que si no acepta el consentimiento, no puede ingresar a la videoconsulta.',
      isWarning: true
    }).then(result => {
      if (result.isConfirmed) {
        navigate(isAuthenticated ? `/${role}/teleconsultas` : '/');
      }
    });
  };

  // Si no está autenticado, redirigir al flujo interno de consentimiento Redplika
  if (!isAuthenticated) {
    return (
      <div className="w-[90vw] md:w-[80vw] mx-auto border border-[#6e787aff] rounded-md flex flex-col items-center justify-center max-h-fit overflow-auto bg-gray-100 p-8 my-4 gap-4">
        <h2 className="text-2xl text-black text-center">{CONSENTIMIENTO_TITULO}</h2>
        <h3 className="text-lg text-black text-center">{CONSENTIMIENTO_SUBTITULO}</h3>
        <h3 className="text-2xl text-black text-center">CONSENTIMIENTO INFORMADO</h3>
        <p className="text-gray-700 text-center max-w-lg">
          Para firmar el consentimiento informado de Redplika, inicia sesión con tu cuenta de paciente o utiliza el enlace que recibiste después de agendar tu cita.
        </p>
        <button
          onClick={() => navigate('/login', { state: { returnTo: `/consentimiento/${appointmentId}` } })}
          className="px-6 py-3 bg-[#1b7ea6ff] text-white rounded-md hover:bg-[#205087ff] transition-colors font-semibold"
        >
          Iniciar sesión para firmar
        </button>
        <button
          onClick={() => navigate('/')}
          className="px-6 py-3 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
        >
          Volver al Inicio
        </button>
      </div>
    );
  }

  return appointmentId && (
    <div className="w-[90vw] md:w-[80vw] mx-auto border border-[#6e787aff] rounded-md flex flex-col items-center justify-center max-h-fit overflow-auto bg-gray-100 p-8 my-4 gap-2">
      <h2 className="text-2xl text-black text-center">{CONSENTIMIENTO_TITULO}</h2>
      <h3 className="text-lg text-black text-center">{CONSENTIMIENTO_SUBTITULO}</h3>
      <h3 className="text-2xl text-black text-center">CONSENTIMIENTO INFORMADO</h3>

      <hr className="mt-4" />

      <div className="w-full max-w-2xl mb-3 space-y-4">
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-left">
          <p className="text-lg font-semibold text-black mb-2">
            Cargar documentos para revisión del médico (opcional)
          </p>
          <p className="text-sm text-gray-700 mb-3">
            Si cuenta con información médica previa que desee compartir con el profesional antes o durante la teleconsulta,
            puede adjuntarla aquí. Esto ayuda al médico a conocer mejor su historial y preparar la atención.
          </p>
          <p className="text-sm font-medium text-gray-800 mb-2">Ejemplos de documentos que puede cargar:</p>
          <ul className="text-sm text-gray-700 list-disc pl-5 space-y-1 mb-3">
            <li>Historias clínicas o epicrisis de consultas anteriores</li>
            <li>Resultados de exámenes de laboratorio</li>
            <li>Imágenes diagnósticas (radiografías, ecografías, resonancias, etc.)</li>
            <li>Fórmulas médicas o órdenes de medicamentos</li>
            <li>Certificados de incapacidad u otros documentos clínicos relevantes</li>
          </ul>
          <p className="text-xs text-gray-500">
            Formatos aceptados: PDF, JPG, PNG, Word (.doc, .docx). Puedes agregar varios archivos uno a uno.
            Esta carga es voluntaria y no reemplaza la firma del consentimiento informado.
          </p>
        </div>

        <InputFile
          fileName="Agregar un archivo"
          onChange={handleFileUpload}
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,application/pdf,image/jpeg,image/png"
        />

        {uploadedFiles.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-2">
            Aún no ha agregado archivos. Si no tiene documentos previos, puede continuar sin cargar nada.
          </p>
        ) : (
          <table className="w-full bg-white rounded-lg overflow-hidden border border-gray-200">
            <thead className="border-b border-[#70706eff] bg-gray-50">
              <tr>
                <th className="text-left border-r border-[#70706eff] px-4 py-2 text-sm">Archivo</th>
                <th className="text-right px-4 py-2 text-sm">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {uploadedFiles.map((file, index) => (
                <tr key={index} className="border-b border-gray-300 last:border-0">
                  <td className="border-r border-gray-300 px-4 py-2 text-sm">{file.name}</td>
                  <td className="px-4 py-2 text-right">
                    <button onClick={() => handleFileRemove(index)} className="button-delete text-sm">
                      Eliminar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <hr className="my-4" />
      <div className="text-left mb-6 space-y-3 text-black">

        <div className="form-group">
          <p><strong>Descripción de la modalidad de consulta:</strong></p>
          <p>
            Consiste en la provisión de servicios de salud a distancia en los componentes de promoción, prevención, diagnóstico, tratamiento y rehabilitación, por profesionales de la salud que utilizan tecnologías de la información y la comunicación, que les permiten intercambiar datos con el propósito de facilitar el acceso y la oportunidad en la prestación de servicios a la población que presenta limitaciones de oferta, de acceso a los servicios o de ambos en su área geográfica. Este procedimiento permite la realización de interconsultas y el acceso de manera más ágil a la evaluación de la historia clínica por médicos especialistas en los términos de la Ley 1419 de 2010.
          </p>
        </div>

        <div className="form-group">
          <p><strong>Declaración del usuario:</strong></p>
          <p>
            Actuando a nombre propio comprendo que, en el curso de mi consulta médica, la información sobre mi persona, la cual en adelante se denominará "información personal", es útil para mi atención médica, autorizo que la misma pueda ser recolectada, almacenada y transmitida a personal médico de {EMPRESA_CONSENTIMIENTO}. Esta información puede incluir datos con los que se me puede identificar, como mi nombre, fecha de nacimiento e historia clínica. Asimismo, autorizo que en caso de que los médicos consideren pertinente me tomen fotografías, graben videos o audio y utilicen cualquier formato multimedia que sea necesario dentro de la modalidad de servicio de TELEMEDICINA, los cuales harán parte integrante de la historia clínica.
            Autorizo la utilización de cualquier equipo biomédico electrónico durante la prestación del servicio de salud en la modalidad de TELEMEDICINA.
            Autorizo que mi información personal pueda ser compartida y utilizada por {EMPRESA_CONSENTIMIENTO} y otros profesionales de la salud para manejar mi caso y asistir en la evaluación, el diagnóstico y el tratamiento de mi condición. Estos profesionales de la salud pueden habitar fuera de mi municipio de residencia y estar ubicados por fuera de la cobertura. Esta información puede ser transmitida en tiempo real utilizando equipos de videoconferencia donde el (los) médico(s) en el CENTRO DE REFERENCIA observarán, participarán y emitirán comentarios o sugerencias a distancia.
          </p>
        </div>

        <div className="form-group">
          <p><strong>Declaración de satisfacción con la información recibida:</strong></p>
          <p>
            Después de entender claramente la información suministrada, de tener la oportunidad de aclarar y resolver mis dudas e inquietudes sobre la modalidad de servicio de TELEMEDICINA, de contar con el espacio y tiempo suficientes, en el cual se me ha explicado satisfactoriamente la naturaleza y propósito de éste, manifiesto libre y voluntariamente mi consentimiento.
            Entiendo que de acuerdo al alcance de la ley vigente, {EMPRESA_CONSENTIMIENTO} no da garantías, seguros o promesas en relación con la seguridad de esta información.
          </p>
        </div>

        <div className="form-group">
          <p><strong>Extensión del presente consentimiento:</strong></p>
          <p>
            Yo consiento que {EMPRESA_CONSENTIMIENTO} puede también utilizar mi información personal en un formulario aparte en el que no se identifica mi persona, para la realización de investigación médica, sin embargo, {EMPRESA_CONSENTIMIENTO} no proveerá mi información personal a terceros sin mi autorización escrita previa, a excepción de la descrita en este consentimiento informado o en caso de ser requerido por la ley.
            Autorizo a {EMPRESA_CONSENTIMIENTO} para que almacene, procese y transmita mi Información Personal a través de medios electrónicos, incluyendo Internet. Los servidores que utiliza {EMPRESA_CONSENTIMIENTO} para almacenar, procesar y transmitir mi Información Personal pueden no localizarse en mi municipio de residencia. {EMPRESA_CONSENTIMIENTO} utiliza medidas de seguridad para proteger la información almacenada, procesada y transmitida a través de medios electrónicos.
          </p>
        </div>

        <div className="form-group">
          <label> <strong>¿Quién firma?</strong> </label>
          <label className="w-fit flex items-center gap-2">
            <input
              type="radio"
              name="userSignature" // Agrupa los radios
              value="paciente"
              checked={typeUserSignature === "paciente"}
              onChange={changeUserSignature}
              className="accent-blue-500"
            />
            Firma paciente
          </label>

          <label className="w-fit flex items-center gap-2">
            <input
              type="radio"
              name="userSignature" // Mismo grupo que el otro radio
              value="representante"
              checked={typeUserSignature === "representante"}
              onChange={changeUserSignature}
              className="accent-blue-500"
            />
            Firma representante legal
          </label>
        </div>

        {typeUserSignature === "paciente" ? (
          <div className="form-group">
            <p><strong>Aceptación de la prestación del servicio</strong></p>
            <p>
              De manera voluntaria y con pleno consentimiento autorizo que mi proceso de atención sea efectuado bajo la modalidad de servicio de Telemedicina, de igual manera estoy de acuerdo con las consultas médicas con especialistas e interconsultas por este medio.
              Al firmar debajo confirmo que he leído, comprendido por completo y estoy de acuerdo con todo lo anterior, o si no soy capaz de leer los párrafos previos, yo confirmo que este consentimiento me ha sido completamente leído y que estoy de acuerdo con todo su contenido.
            </p>
          </div>
        ) : (
          <div className="form-group">
            <p><strong>Consentimiento informado diferido:</strong></p>
            <p>
              Este numeral debe ser diligenciado por el representante legal del usuario cuando éste no tiene la capacidad legal para otorgar su consentimiento (menor de edad, inconsciencia, alteración mental temporal o definitiva).
              Dado que el beneficiario de los procedimientos es considerado incapaz o incompetente para tomar o rechazar la decisión o dar su consentimiento, el profesional me ha explicado, en mi calidad de representante legal, en forma clara, expresa y satisfactoria en qué consiste la telemedicina y sus implicaciones. He comprendido todo lo anterior y por ello de manera libre y voluntaria yo, &nbsp;

              <input type="text" className="px-1 border-b border-black focus:outline-none" placeholder="Nombre representante legal"
                name="fullName" value={dataRepresentante.fullName} onChange={onHandleChangeDataRepresentante}
              />

              &nbsp; identificado (a) con C.C. &nbsp;

              <input type="number" maxLength={10} className="px-1 border-b border-black focus:outline-none" placeholder="N.° cédula representante"
                name="idNumber" value={dataRepresentante.idNumber} onChange={onHandleChangeDataRepresentante}
              />

              &nbsp;, en mi calidad de &nbsp;

              <select name="relationship" className="border-b border-black focus:outline-none" onChange={onHandleChangeDataRepresentante}>
                <option value="">Parentezco</option>
                <option value="madre">Madre</option>
                <option value="padre">Padre</option>
                <option value="hermano">Hermano(a)</option>
                <option value="hijo">Hijo(a)</option>
                <option value="otros">Otros</option>
              </select>

              &nbsp; de <strong>{appointmentInfo.firstName + " " + appointmentInfo.firstLastName}</strong>, doy mi consentimiento para que se le realice esta modalidad de consulta.
            </p>
          </div>
        )}

      </div>

      {/* Canvas para la firma digital */}
      <div className="mb-3 w-full max-w-2xl">
        <p className="form-label text-black font-semibold mb-2">Firma del {typeUserSignature === "paciente" ? "paciente" : "representante legal"}:</p>
        
        {/* Instrucciones para firmar */}
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4 rounded-r">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h4 className="text-sm font-semibold text-blue-800 mb-2">¿Cómo firmar el consentimiento?</h4>
              <div className="text-sm text-blue-700 space-y-1">
                <p><strong>1. En computadora:</strong> Haz clic y arrastra el mouse sobre el recuadro blanco para dibujar tu firma.</p>
                <p><strong>2. En dispositivo móvil o tablet:</strong> Toca y desliza tu dedo sobre el recuadro blanco para dibujar tu firma.</p>
                <p><strong>3. Si te equivocas:</strong> Haz clic en el botón "Limpiar Firma" para borrar y empezar de nuevo.</p>
                <p><strong>4. Una vez que estés satisfecho con tu firma:</strong> Haz clic en el botón "Aceptar" para enviar el consentimiento.</p>
              </div>
            </div>
          </div>
        </div>

        <div 
          className="border-2 border-gray-300 rounded"
          data-signature-canvas
          style={{ 
            touchAction: 'none',
            position: 'relative',
            backgroundColor: '#f9f9f9'
          }}
        >
          <SignatureCanvas
            ref={sigCanvasRef}
            canvasProps={{
              className: 'w-full',
              style: {
                touchAction: 'none',
                cursor: 'crosshair',
                backgroundColor: '#f9f9f9'
              }
            }}
            backgroundColor="#f9f9f9"
            penColor="#000000"
            minWidth={1}
            maxWidth={1.5}
            velocityFilterWeight={0.7}
          />
        </div>
        <p className="text-xs text-gray-500 mt-2 text-center">
          Dibuja tu firma en el recuadro de arriba usando el mouse o tu dedo
        </p>
      </div>

      {/* Botones de Aceptar y Rechazar */}
      <div className="flex space-x-4">
        <button
          onClick={handleAccept}
          className="button-send"
        >
          Aceptar
        </button>
        <button
          onClick={clearSignature}
          className="button-add"
        >
          Limpiar Firma
        </button>
        <button
          onClick={handleDecline}
          className="button-gray"
        >
          Rechazar
        </button>
      </div>
    </div>
  );

};

export default ConsentimientoInformado;